import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import GettingStartedChecklist from '@/components/GettingStartedChecklist';
import EventModeBanner from '@/components/EventModeBanner';
import useEventMode from '@/hooks/useEventMode';
import useFirstDashboardVisit from '@/hooks/useFirstDashboardVisit';
import axios from 'axios';
import { toast } from 'sonner';
import { toolsHubPath } from '@/lib/toolRoutes';
import {
  TrendUp, Buildings, ChartBar, FileText, ChatCircleDots,
  SignOut, Plus, ArrowRight, CaretRight, Sparkle
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { enabled: eventMode } = useEventMode();

  useFirstDashboardVisit();

  useEffect(() => { 
    fetchDashboardStats();
    fetchPlanInfo();
    // Check for payment success - verify with backend (polling)
    const sessionId = searchParams.get('session_id');
    if (searchParams.get('payment') === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (searchParams.get('payment') === 'success') {
      toast.success(language === 'tr' ? 'Ödeme başarılı! Planınız yükseltildi.' : 'Payment successful! Your plan has been upgraded.');
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;
    if (attempt >= MAX_ATTEMPTS) {
      toast.info(language === 'tr'
        ? 'Ödeme işleniyor, birkaç dakika içinde planınız güncellenecek.'
        : 'Payment is still processing, your plan will be updated shortly.');
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/payments/status/${sessionId}`, { headers: getAuthHeaders() });
      if (data.payment_status === 'paid') {
        toast.success(language === 'tr' ? 'Ödeme başarılı! Planınız yükseltildi.' : 'Payment successful! Your plan has been upgraded.');
        fetchPlanInfo();
        return;
      }
      if (data.status === 'expired') {
        toast.error(language === 'tr' ? 'Ödeme oturumu sona erdi.' : 'Payment session expired.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), INTERVAL_MS);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), INTERVAL_MS);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally { setLoading(false); }
  };

  const fetchPlanInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/plan/info`, { headers: getAuthHeaders() });
      setPlanInfo(response.data);
    } catch (error) { /* ignore */ }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#16A34A]';
    if (score >= 60) return 'text-[#0F1B36]';
    if (score >= 40) return 'text-[#CA8A04]';
    return 'text-[#E60000]';
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: 'bg-[#16A34A]/10 text-[#16A34A]',
      in_progress: 'bg-[#0F1B36]/10 text-[#0F1B36]',
      draft: 'bg-[#52525B]/10 text-[#52525B]'
    };
    return colors[status] || colors.draft;
  };

  const getStatusText = (status) => {
    const map = { completed: t('status.completed'), in_progress: t('status.in_progress'), draft: t('status.draft') };
    return map[status] || status;
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <EventModeBanner />
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#09090B] font-medium" data-testid="nav-dashboard">{t('nav.dashboard')}</Link>
              <Link to="/companies" className="text-[#52525B] hover:text-[#09090B] transition-colors" data-testid="nav-companies">{t('nav.companies')}</Link>
              <Link to={toolsHubPath(language)} className="text-[#52525B] hover:text-[#09090B] transition-colors" data-testid="nav-tools">{t('nav.tools')}</Link>
              <Link to="/advisor" className="text-[#52525B] hover:text-[#09090B] transition-colors" data-testid="nav-advisor">{t('nav.advisor')}</Link>
              {user?.is_admin && (
                <Link to="/admin" className="text-[#52525B] hover:text-[#09090B] transition-colors" data-testid="nav-admin">{t('nav.admin')}</Link>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 bg-[#0F1B36] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                  </div>
                  <span className="hidden md:inline text-[#09090B]">{user?.first_name} {user?.last_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-[#52525B]">{user?.organization_name || '-'}</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-[#E60000]" data-testid="logout-btn">
                  <SignOut size={16} className="mr-2" />{t('nav.signout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="heading-2 text-[#09090B] mb-2" data-testid="dashboard-title">
            {t('dashboard.welcome', { name: user?.first_name })}
          </h1>
          <p className="text-[#52525B]">
            {user?.organization_name ? t('dashboard.subtitle', { org: user.organization_name }) : t('dashboard.subtitle_default')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#52525B]">{t('common.loading')}</div>
        ) : (
          <>
            <GettingStartedChecklist />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-[#E4E4E7] p-6" data-testid="stat-companies">
                <div className="flex items-center gap-3 mb-4">
                  <Buildings size={24} className="text-[#0F1B36]" />
                  <span className="label-text text-[#52525B]">{t('dashboard.stats.companies')}</span>
                </div>
                <p className="text-4xl font-bold text-[#09090B]">{stats?.total_companies || 0}</p>
              </div>
              <div className="bg-white border border-[#E4E4E7] p-6" data-testid="stat-assessments">
                <div className="flex items-center gap-3 mb-4">
                  <ChartBar size={24} className="text-[#0F1B36]" />
                  <span className="label-text text-[#52525B]">{t('dashboard.stats.assessments')}</span>
                </div>
                <p className="text-4xl font-bold text-[#09090B]">{stats?.total_assessments || 0}</p>
              </div>
              <div className="bg-white border border-[#E4E4E7] p-6" data-testid="stat-completed">
                <div className="flex items-center gap-3 mb-4">
                  <FileText size={24} className="text-[#16A34A]" />
                  <span className="label-text text-[#52525B]">{t('dashboard.stats.completed')}</span>
                </div>
                <p className="text-4xl font-bold text-[#09090B]">{stats?.completed_assessments || 0}</p>
              </div>
              <div className="bg-white border border-[#E4E4E7] p-6" data-testid="stat-avg-score">
                <div className="flex items-center gap-3 mb-4">
                  <TrendUp size={24} className="text-[#0F1B36]" />
                  <span className="label-text text-[#52525B]">{t('dashboard.stats.avg_score')}</span>
                </div>
                <p className={`text-4xl font-bold ${getScoreColor(stats?.average_score || 0)}`}>{stats?.average_score || 0}</p>
              </div>
            </div>

            {/* Plan Info */}
            {planInfo && (
              <div className="bg-white border border-[#E4E4E7] p-6 mb-8" data-testid="plan-info">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="heading-4 text-[#09090B]">{t('plan.title')}</h2>
                  {eventMode ? (
                    <span className="text-xs font-bold uppercase px-3 py-1 bg-[#3CD9B8]/15 text-[#0F1B36] inline-flex items-center gap-1.5" data-testid="plan-event-badge">
                      <Sparkle size={12} weight="fill" className="text-[#3CD9B8]" />
                      {language === 'tr' ? 'ETKİNLİK ERİŞİMİ' : 'EVENT ACCESS'}
                    </span>
                  ) : (
                    <span className={`text-xs font-bold uppercase px-3 py-1 ${
                      planInfo.plan_type === 'advanced' ? 'bg-[#0F1B36]/10 text-[#0F1B36]'
                      : planInfo.plan_type === 'basic' ? 'bg-[#16A34A]/10 text-[#16A34A]'
                      : 'bg-[#F4F4F5] text-[#52525B]'
                    }`}>
                      {planInfo.plan_type.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-[#52525B] mb-1">{t('plan.assessments')}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-bold text-[#09090B]">{planInfo.assessments_used}</span>
                      <span className="text-sm text-[#52525B] mb-1">{t('plan.of')} {planInfo.assessments_limit === 999 ? '∞' : planInfo.assessments_limit}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#F4F4F5] w-full">
                      <div className="h-full bg-[#0F1B36]" style={{width: `${Math.min((planInfo.assessments_used / planInfo.assessments_limit) * 100, 100)}%`}} />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-[#52525B] mb-1">{t('plan.companies')}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-bold text-[#09090B]">{planInfo.companies_used}</span>
                      <span className="text-sm text-[#52525B] mb-1">{t('plan.of')} {planInfo.companies_limit === 999 ? '∞' : planInfo.companies_limit}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#F4F4F5] w-full">
                      <div className="h-full bg-[#0F1B36]" style={{width: `${Math.min((planInfo.companies_used / planInfo.companies_limit) * 100, 100)}%`}} />
                    </div>
                  </div>
                </div>
                {planInfo.plan_type === 'free' && !eventMode && (
                  <Link to="/pricing" className="inline-flex items-center gap-2 mt-4 text-sm text-[#0F1B36] hover:text-[#00227A] font-medium" data-testid="plan-upgrade-link">
                    {t('plan.upgrade')} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-[#E4E4E7] p-6">
                <h2 className="heading-4 text-[#09090B] mb-6">{t('dashboard.quick_actions')}</h2>
                <div className="space-y-4">
                  <Link to="/companies" className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="action-add-company">
                    <div className="flex items-center gap-3">
                      <Plus size={20} className="text-[#0F1B36]" />
                      <span className="font-medium text-[#09090B]">{t('dashboard.action.add_company')}</span>
                    </div>
                    <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                  </Link>
                  <Link to="/advisor" className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="action-chat-advisor">
                    <div className="flex items-center gap-3">
                      <ChatCircleDots size={20} className="text-[#0F1B36]" />
                      <span className="font-medium text-[#09090B]">{t('dashboard.action.chat_advisor')}</span>
                    </div>
                    <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                  </Link>
                  <Link to="/stores" className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="action-stores">
                    <div className="flex items-center gap-3">
                      <TrendUp size={20} className="text-[#0F1B36]" />
                      <span className="font-medium text-[#09090B]">
                        {language === 'tr' ? 'URL ile Mağaza Analizi' : 'URL Store Analysis'}
                      </span>
                    </div>
                    <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                  </Link>
                  <Link to={toolsHubPath(language)} className="flex items-center justify-between p-4 border border-[#3CD9B8] bg-[#3CD9B8]/5 hover:border-[#0F1B36] transition-colors group" data-testid="action-tools">
                    <div className="flex items-center gap-3">
                      <Sparkle size={20} className="text-[#0F1B36]" weight="fill" />
                      <span className="font-medium text-[#09090B]">
                        {language === 'tr' ? 'Ücretsiz büyüme araçları' : 'Free growth tools'}
                      </span>
                    </div>
                    <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                  </Link>
                  <Link
                    to={language === 'tr' ? '/araclar/gecmis' : '/tools/history'}
                    className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group"
                    data-testid="action-tool-history"
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-[#0F1B36]" />
                      <span className="font-medium text-[#09090B]">
                        {language === 'tr' ? 'Araç geçmişim' : 'My tool history'}
                      </span>
                    </div>
                    <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                  </Link>
                  {!eventMode && (
                    <Link to="/pricing" className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors group" data-testid="action-upgrade">
                      <div className="flex items-center gap-3">
                        <TrendUp size={20} className="text-[#0F1B36]" />
                        <span className="font-medium text-[#09090B]">{t('dashboard.action.upgrade')}</span>
                      </div>
                      <CaretRight size={16} className="text-[#52525B] group-hover:text-[#0F1B36]" />
                    </Link>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2 bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="heading-4 text-[#09090B]">{t('dashboard.recent')}</h2>
                  {stats?.recent_assessments?.length > 0 && (
                    <Link to="/companies" className="text-[#0F1B36] hover:text-[#00227A] text-sm font-medium flex items-center gap-1">
                      {t('dashboard.view_all')} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>

                {stats?.recent_assessments?.length > 0 ? (
                  <div className="space-y-4">
                    {stats.recent_assessments.map((assessment) => (
                      <div key={assessment.id} className="flex items-center justify-between p-4 border border-[#E4E4E7] hover:border-[#0F1B36] transition-colors" data-testid={`assessment-row-${assessment.id}`}>
                        <div>
                          <p className="font-medium text-[#09090B]">{assessment.company_name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-[#52525B] uppercase">{assessment.type}</span>
                            <span className={`text-xs px-2 py-0.5 ${getStatusBadge(assessment.status)}`}>
                              {getStatusText(assessment.status)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {assessment.status === 'completed' && (
                            <span className={`text-2xl font-bold ${getScoreColor(assessment.score)}`}>{assessment.score}</span>
                          )}
                          {assessment.status === 'completed' ? (
                            <Link to={`/report/${assessment.id}`} className="text-[#0F1B36] hover:text-[#00227A] font-medium text-sm">
                              {t('dashboard.view_report')}
                            </Link>
                          ) : (
                            <Link to={`/assessment/${assessment.company_id}/${assessment.id}`} className="text-[#0F1B36] hover:text-[#00227A] font-medium text-sm">
                              {t('dashboard.continue')}
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ChartBar size={48} className="text-[#E4E4E7] mx-auto mb-4" />
                    <p className="text-[#52525B] mb-4">{t('dashboard.no_assessments')}</p>
                    <Link to="/companies" className="btn-primary inline-flex items-center gap-2" data-testid="cta-start-assessment">
                      {t('dashboard.start_first')} <ArrowRight size={16} />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
